/**
 * Keep `--app-viewport-height` equal to the height the visitor can actually see.
 *
 * On iOS Safari `100vh` is the height the viewport would have with the browser
 * chrome collapsed, which it usually is not — trusting it puts the tab bar below
 * the fold on first paint and again every time the address bar animates. The
 * visual viewport is the only measurement that matches what is on screen.
 *
 * The standalone/browser classes let the shell reclaim the space Safari's chrome
 * occupies once the app is installed to the home screen.
 *
 * WHAT THE VISUAL VIEWPORT IS NOT GOOD FOR is the soft keyboard. It shrinks by the
 * keyboard's height, and iOS hands that shrunken number back — stale, with the keyboard
 * long gone — when the app resumes from the background, with no later resize to correct
 * it. `.app-shell` is sized from this property, so believing it leaves the app ending
 * mid-screen with the tab bar floating in the middle of the page and the canvas carrying
 * on below it. (Reported and fixed first in the sibling habb.me app, which pins its shell
 * the same way: SPEC-047#AC-3.7.g there.)
 *
 * So the dynamic viewport is the FLOOR. `100dvh` is the viewport as the browser's own
 * chrome has left it — it tracks the address bar, which is the whole point of this module,
 * and it is not moved by the keyboard — so it agrees with the visual viewport in every
 * case this module was written for, and disagrees only when the keyboard is up or the
 * reading is stale. A shorter visual viewport is believed only while an editable element
 * holds focus, which is the one thing that makes a keyboard's worth of missing height a
 * real measurement rather than a leftover one.
 */

/**
 * The controls that raise a soft keyboard — deliberately narrow, so a focused checkbox or
 * button, which do not, cannot license a short reading.
 */
const SOFT_KEYBOARD_FOCUS_SELECTOR = [
    'input:not([type="button"]):not([type="submit"]):not([type="reset"])'
        + ':not([type="checkbox"]):not([type="radio"]):not([type="range"])'
        + ':not([type="color"]):not([type="file"])',
    'textarea',
    '[contenteditable]:not([contenteditable="false"])',
].join(',');

export function hasSoftKeyboardFocus(element) {
    return Boolean(
        element
        && typeof element.matches === 'function'
        && element.matches(SOFT_KEYBOARD_FOCUS_SELECTOR),
    );
}

/**
 * `100dvh` in pixels. No API reports the dynamic viewport, so it is measured off a probe
 * that exists for one layout read. 0 means "not measurable" — jsdom, or a browser without
 * `dvh` — and the caller then simply has no floor to apply.
 */
function measureDynamicViewportHeight(windowObject) {
    const documentElement = windowObject.document.documentElement;
    const probe = windowObject.document.createElement('div');

    probe.setAttribute('aria-hidden', 'true');
    probe.style.cssText = 'position:fixed;top:0;left:0;width:0;height:100dvh;'
        + 'visibility:hidden;pointer-events:none;';
    documentElement.appendChild(probe);

    const height = probe.getBoundingClientRect().height;

    probe.remove();

    return height > 0 ? height : 0;
}

/**
 * The height to publish, in CSS pixels. 0 means nothing was measurable and the caller
 * should leave the property alone rather than pin the shell to zero.
 */
export function resolveViewportHeight({
    visualViewportHeight = 0,
    innerHeight = 0,
    dynamicViewportHeight = 0,
    isSoftKeyboardOpen = false,
} = {}) {
    const toHeight = (value) => (typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0);
    const measured = toHeight(visualViewportHeight) || toHeight(innerHeight);
    const dynamicViewport = toHeight(dynamicViewportHeight);

    if (!measured) {
        return dynamicViewport;
    }

    if (!dynamicViewport) {
        return measured;
    }

    return measured < dynamicViewport && !isSoftKeyboardOpen ? dynamicViewport : measured;
}

/**
 * `trustsSoftKeyboard: false` reads the viewport as if no keyboard were up, whatever holds
 * focus. That is the resume case: the field focused in the previous session is not evidence
 * of a keyboard in this one, and a keyboard that genuinely reopens says so with its own
 * resize.
 */
function syncViewportHeight(windowObject, { trustsSoftKeyboard = true } = {}) {
    const viewportHeight = resolveViewportHeight({
        visualViewportHeight: windowObject.visualViewport?.height,
        innerHeight: windowObject.innerHeight,
        dynamicViewportHeight: measureDynamicViewportHeight(windowObject),
        isSoftKeyboardOpen: trustsSoftKeyboard
            && hasSoftKeyboardFocus(windowObject.document.activeElement),
    });

    if (viewportHeight > 0) {
        windowObject.document.documentElement.style.setProperty(
            '--app-viewport-height',
            `${Math.round(viewportHeight)}px`,
        );
    }
}

function syncDisplayModeClasses(windowObject) {
    const isStandalone = windowObject.matchMedia('(display-mode: standalone)').matches
        || windowObject.navigator.standalone === true;

    windowObject.document.documentElement.classList.toggle('is-standalone', isStandalone);
}

export function startViewportSync(windowObject = typeof window === 'undefined' ? null : window) {
    if (!windowObject) {
        return;
    }

    const onViewportChange = () => {
        syncDisplayModeClasses(windowObject);
        syncViewportHeight(windowObject);
    };

    const resync = () => {
        syncDisplayModeClasses(windowObject);
        syncViewportHeight(windowObject, { trustsSoftKeyboard: false });
        // iOS reports a stale height on the tick it resumes from the background,
        // so re-measure once layout has settled.
        windowObject.requestAnimationFrame(() => {
            syncViewportHeight(windowObject, { trustsSoftKeyboard: false });
        });
    };

    resync();

    windowObject.visualViewport?.addEventListener('resize', onViewportChange);
    windowObject.addEventListener('resize', onViewportChange);
    windowObject.addEventListener('orientationchange', onViewportChange);
    // The keyboard closing is not reliably a resize, and the shell must not stay short once
    // the thing that licensed it being short has gone. Next frame, not this one: during
    // `focusout` the focus has not landed yet, and moving between two fields keeps the
    // keyboard up — only what holds focus after it settles says whether the short reading
    // still stands.
    windowObject.addEventListener('focusout', () => {
        windowObject.requestAnimationFrame(() => syncViewportHeight(windowObject));
    });
    windowObject.addEventListener('pageshow', resync);
    windowObject.document.addEventListener('visibilitychange', () => {
        if (windowObject.document.visibilityState === 'visible') {
            resync();
        }
    });
}
